// LEGACY — superseded by features/client-rental-config/. Scheduled for deletion in 2 sprints.
import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  getClientRentalConfigByClientId as getClientRentalConfigByClientIdApi,
  updateClientRentalConfig as updateClientRentalConfigApi,
  createClientRentalConfig as createClientRentalConfigApi
} from "../../pages/Clients/ClientRentalConfig/api";

export const getClientRentalConfigs = createAsyncThunk(
  "clientRentalConfig/getClientRentalConfigs",
  async (_, { rejectWithValue }) => {
    try {
      // TODO: Implement when API endpoint is available
      return rejectWithValue("Client rental configs API not yet implemented");
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch client rental configs");
    }
  }
);

export const getClientRentalConfigByClientId = createAsyncThunk(
  "clientRentalConfig/getClientRentalConfigByClientId",
  async (clientId: string | number, { rejectWithValue }) => {
    try {
      console.log("Fetching client rental config for clientId:", clientId);
      const response = await getClientRentalConfigByClientIdApi(clientId);
      console.log("Client rental config API response (raw):", response);
      console.log("Response type:", typeof response);
      console.log("Response is null:", response === null);
      console.log("Response is undefined:", response === undefined);
      console.log("Response is array:", Array.isArray(response));
      console.log("Response keys:", response && typeof response === 'object' && !Array.isArray(response) ? Object.keys(response) : 'not an object');
      
      // Axios interceptor already extracts response.data, so response IS the data object
      // The response should be the JSON object directly (as shown in network tab)
      let configData = response;
      
      // If response is null or undefined, return null
      if (configData === null || configData === undefined) {
        console.log("Response is null/undefined - returning null");
        return null;
      }
      
      // Check if response is a "not found" message (graceful handling)
      // API returns {message: "No rental configuration override found for this client"} when no config exists
      if (configData && typeof configData === 'object' && !Array.isArray(configData)) {
        const message = configData.message || configData.Message;
        if (message && typeof message === 'string' && 
            (message.toLowerCase().includes('no rental configuration') || 
             message.toLowerCase().includes('not found') ||
             message.toLowerCase().includes('no configuration'))) {
          console.log("No rental configuration found (graceful response) - returning null");
          return null; // Treat as "no config exists" - not an error
        }
      }
      
      // If response is an empty object, return null
      if (typeof configData === 'object' && !Array.isArray(configData) && Object.keys(configData).length === 0) {
        console.log("Response is empty object - returning null");
        return null;
      }
      
      // If response has a data property (double-wrapped), extract it
      if (configData && typeof configData === 'object' && !Array.isArray(configData) && 'data' in configData && configData.data !== undefined) {
        console.log("Response has data property - extracting");
        configData = configData.data;
        
        // Check again for "not found" message after extraction
        if (configData && typeof configData === 'object' && !Array.isArray(configData)) {
          const message = configData.message || configData.Message;
          if (message && typeof message === 'string' && 
              (message.toLowerCase().includes('no rental configuration') || 
               message.toLowerCase().includes('not found') ||
               message.toLowerCase().includes('no configuration'))) {
            console.log("No rental configuration found (after extraction) - returning null");
            return null;
          }
        }
      }
      
      // If response is an array (shouldn't happen, but handle it)
      if (Array.isArray(configData)) {
        console.log("Response is an array - taking first element");
        configData = configData.length > 0 ? configData[0] : null;
      }
      
      // Final validation: ensure configData has required fields (clientId) to be considered valid
      if (configData && typeof configData === 'object' && !Array.isArray(configData)) {
        // If it doesn't have clientId, it's likely not a valid config object
        if (!configData.clientId && !configData.id) {
          console.log("Response doesn't have clientId or id - treating as no config found");
          return null;
        }
      }
      
      console.log("Final config data to return:", configData);
      console.log("Config data type:", typeof configData);
      console.log("Config data is null:", configData === null);
      console.log("Config data has clientId:", configData?.clientId);
      console.log("Config data keys:", configData && typeof configData === 'object' && !Array.isArray(configData) ? Object.keys(configData) : 'not an object');
      
      // Ensure we return the data object, not null if it exists
      return configData;
    } catch (error: any) {
      // NOTE: Axios interceptor converts 404 errors to string: "Sorry! the data you are looking for could not be found"
      // So error might be a string, not an object with error.response
      
      // PRIORITY 1: Check if error is a string with "not found" message (from axios interceptor)
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.response?.data?.message || error?.response?.data || '';
      
      // Check if this is a "not found" case (normal, not an error)
      if (errorMessage && typeof errorMessage === 'string') {
        const lowerMessage = errorMessage.toLowerCase();
        if (lowerMessage.includes('not found') || 
            lowerMessage.includes('could not be found') ||
            lowerMessage.includes('no rental configuration') ||
            lowerMessage.includes('no configuration') ||
            (lowerMessage.includes('rental configuration') && lowerMessage.includes('not found'))) {
          // This is a normal "no config exists" case - don't log as error, just return null
          console.log("No rental configuration found - treating as normal 'no config exists' case");
          return null; // Return null to indicate no config exists - this will trigger fulfilled, not rejected
        }
      }
      
      // PRIORITY 2: Handle 404 status code (if error object has response property)
      if (error?.response?.status === 404) {
        console.log("Config not found (404) - treating as normal 'no config exists' case");
        return null; // Return null to indicate no config exists - this will trigger fulfilled, not rejected
      }
      
      // PRIORITY 3: Handle timeout errors gracefully
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        console.warn("Client rental config API timeout - returning null");
        return null;
      }
      
      // Only log and reject for actual errors (not "not found" cases)
      console.error("Client rental config API error:", error);
      console.error("Error response:", error?.response);
      console.error("Error status:", error?.response?.status);
      console.error("Error data:", error?.response?.data);
      console.error("Client rental config fetch failed:", error?.response?.data || error?.message || error);
      return rejectWithValue(error?.response?.data || error?.message || error || "Failed to fetch client rental config");
    }
  }
);

export const updateClientRentalConfig = createAsyncThunk(
  "clientRentalConfig/updateClientRentalConfig",
  async (config: any, { rejectWithValue }) => {
    try {
      // Check if this is a new config (no id) or existing config (has id)
      if (!config.id) {
        // New config - try POST (create) first
        // POST payload should include tenantId and clientId
        try {
          const response = await createClientRentalConfigApi(config);
          toast.success("Client Rental Configuration created successfully", {
            autoClose: 3000,
          });
          return response.data || response;
        } catch (createError: any) {
          // If POST fails because config already exists, fetch it and retry as PUT
          const errorMessage = createError?.response?.data?.message || createError?.message || "";
          if (errorMessage.includes("already exists") || errorMessage.includes("Use Update instead")) {
            console.log("Config already exists, fetching existing config to update...");
            // Fetch the existing config to get its ID
            const existingConfig = await getClientRentalConfigByClientIdApi(config.clientId);
            const existingConfigData = existingConfig.data || existingConfig;
            
            if (existingConfigData && existingConfigData.id) {
              // Use PUT to update the existing config
              // PUT payload should NOT include tenantId and clientId
              const { tenantId, clientId, ...updateData } = config;
              const response = await updateClientRentalConfigApi(existingConfigData.id, updateData);
              toast.success("Client Rental Configuration updated successfully", {
                autoClose: 3000,
              });
              return response.data || response;
            } else {
              throw new Error("Failed to fetch existing configuration");
            }
          }
          // Re-throw other errors
          throw createError;
        }
      } else {
        // Existing config - use PUT (update)
        // PUT payload should NOT include tenantId and clientId (according to API docs)
        const { tenantId, clientId, ...updateData } = config;
        const response = await updateClientRentalConfigApi(config.id, updateData);
        toast.success("Client Rental Configuration updated successfully", {
          autoClose: 3000,
        });
        return response.data || response;
      }
    } catch (error: any) {
      const errorMessage = config.id 
        ? "Client Rental Configuration Update Failed"
        : "Client Rental Configuration Creation Failed";
      toast.error(errorMessage, {
        autoClose: 3000,
      });
      return rejectWithValue(error?.response?.data || error?.message || "Failed to save client rental config");
    }
  }
);
